import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FiCreditCard,
  FiEdit,
  FiRotateCcw,
  FiUser,
  FiX,
  FiXCircle,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";

import {
  config,
} from "../../../../../../utils/apiUrl";

import EmployeeStatusBadge
  from "../common/EmployeeStatusBadge";

import {
  formatEmployeeCurrency,
  formatEmployeeDate,
  formatEmployeeLabel,
} from "../utils/employee.utils";

import "./EmployeeAdvance.css";


const AdvanceDetailsDrawer = ({
  open,

  advance,

  onClose,

  onEdit,

  onRecover,

  onCancel,
}) => {

  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  const [
    recoveries,
    setRecoveries,
  ] =
    useState([]);


  const [
    loading,
    setLoading,
  ] =
    useState(false);


  useEffect(
    () => {

      if (
        !open ||
        !advance?._id
      ) {

        return;

      }


      const loadRecoveries =
        async () => {

          try {

            setLoading(
              true
            );


            const response =
              await api.get(
                config.getEmployeeAdvanceRecoveries(
                  advance._id
                )
              );


            if (
              response?.status ===
              "success"
            ) {

              setRecoveries(
                Array.isArray(
                  response?.data
                )
                  ?
                  response.data
                  :
                  response
                    ?.data
                    ?.recoveries ||
                  []
              );

            }

          } catch (
            requestError
          ) {

            console.error(
              "Advance recoveries error:",
              requestError
            );


            setRecoveries(
              []
            );

          } finally {

            setLoading(
              false
            );

          }

        };


      loadRecoveries();

    },
    [
      api,
      open,
      advance,
    ]
  );


  if (
    !open ||
    !advance
  ) {

    return null;

  }


  const employee =
    advance?.employeeId &&
    typeof advance.employeeId ===
      "object"
      ?
      advance.employeeId
      :
      advance?.employee ||
      {};


  const advanceAmount =
    Number(
      advance?.advanceAmount ??
      advance?.amount ??
      0
    );


  const recoveredAmount =
    Number(
      advance?.recoveredAmount ??
      advance?.totalRecoveredAmount ??
      0
    );


  const balanceAmount =
    Number(
      advance?.balanceAmount ??
      Math.max(
        advanceAmount -
        recoveredAmount,
        0
      )
    );


  const status =
    advance?.status ||
    (
      balanceAmount <=
      0
        ?
        "RECOVERED"
        :
        recoveredAmount >
        0
          ?
          "PARTIALLY_RECOVERED"
          :
          "ACTIVE"
    );


  const closed =
    [
      "RECOVERED",
      "CANCELLED",
    ].includes(
      String(
        status
      ).toUpperCase()
    );


  return ReactDOM
    .createPortal(

      <div className="employee-advance-drawer-root">

        <button
          type="button"
          className="employee-advance-drawer-backdrop"
          onClick={
            onClose
          }
          aria-label="Close"
        />


        <aside className="employee-advance-drawer">

          <header>

            <div>

              <span>
                EMPLOYEE ADVANCE
              </span>

              <h2>
                Advance Details
              </h2>

            </div>


            <button
              type="button"
              onClick={
                onClose
              }
            >
              <FiX />
            </button>

          </header>


          <div className="employee-advance-drawer-body">

            <section className="employee-advance-drawer-profile">

              <div className="employee-advance-drawer-avatar">

                <FiUser />

              </div>


              <div>

                <small>
                  {
                    employee?.employeeCode ||
                    advance?.employeeCode ||
                    "-"
                  }
                </small>

                <h3>
                  {
                    employee?.name ||
                    advance?.employeeName ||
                    "-"
                  }
                </h3>

                <p>
                  {
                    formatEmployeeDate(
                      advance?.advanceDate ||
                      advance?.createdAt
                    )
                  }
                </p>

              </div>


              <EmployeeStatusBadge
                status={
                  status
                }
              />

            </section>


            <section className="employee-advance-detail-section">

              <h4>
                Advance Summary
              </h4>


              <div className="employee-advance-detail-grid">

                <AmountBox
                  label="Advance Amount"
                  value={
                    advanceAmount
                  }
                />

                <AmountBox
                  label="Recovered"
                  value={
                    recoveredAmount
                  }
                  positive
                />

                <AmountBox
                  label="Outstanding"
                  value={
                    balanceAmount
                  }
                  warning={
                    balanceAmount >
                    0
                  }
                />

              </div>

            </section>


            <section className="employee-advance-detail-section">

              <h4>
                Recovery Configuration
              </h4>


              <div className="employee-advance-info-grid">

                <div>

                  <span>
                    Recovery Type
                  </span>

                  <strong>
                    {
                      formatEmployeeLabel(
                        advance?.recoveryType ||
                        "MANUAL"
                      )
                    }
                  </strong>

                </div>


                <div>

                  <span>
                    Monthly Recovery
                  </span>

                  <strong>
                    {
                      formatEmployeeCurrency(
                        advance
                          ?.recoveryAmountPerMonth ??
                        advance
                          ?.monthlyRecoveryAmount ??
                        0
                      )
                    }
                  </strong>

                </div>

              </div>

            </section>


            <section className="employee-advance-detail-section">

              <h4>
                Reason
              </h4>

              <p className="employee-advance-reason">
                {
                  advance?.reason ||
                  advance?.purpose ||
                  "-"
                }
              </p>

            </section>


            <section className="employee-advance-detail-section">

              <h4>
                Recovery History
              </h4>


              {
                loading
                  ?
                  (
                    <div className="employee-advance-history-empty">
                      Loading recovery history...
                    </div>
                  )
                  :
                  recoveries.length ===
                    0
                    ?
                    (
                      <div className="employee-advance-history-empty">

                        <FiCreditCard />

                        <strong>
                          No recovery recorded
                        </strong>

                      </div>
                    )
                    :
                    (
                      <div className="employee-advance-history-list">

                        {
                          recoveries.map(
                            (
                              recovery
                            ) => (

                              <article
                                key={
                                  recovery._id
                                }
                              >

                                <div>

                                  <strong>
                                    {
                                      formatEmployeeCurrency(
                                        recovery.amount
                                      )
                                    }
                                  </strong>

                                  <span>
                                    {
                                      formatEmployeeLabel(
                                        recovery.recoveryMode ||
                                        recovery.paymentMode ||
                                        recovery.mode
                                      )
                                    }
                                  </span>

                                </div>


                                <div>

                                  <strong>
                                    {
                                      formatEmployeeDate(
                                        recovery.recoveryDate ||
                                        recovery.createdAt
                                      )
                                    }
                                  </strong>

                                  <span>
                                    {
                                      recovery.referenceNumber ||
                                      "-"
                                    }
                                  </span>

                                </div>

                              </article>

                            )
                          )
                        }

                      </div>
                    )
              }

            </section>

          </div>


          <footer>

            <button
              type="button"
              onClick={
                () =>
                  onEdit?.(
                    advance
                  )
              }
              disabled={
                closed
              }
            >

              <FiEdit />

              Edit

            </button>


            <button
              type="button"
              className="employee-advance-drawer-cancel"
              onClick={
                () =>
                  onCancel?.(
                    advance
                  )
              }
              disabled={
                closed
              }
            >

              <FiXCircle />

              Cancel

            </button>


            <button
              type="button"
              className="employee-advance-drawer-recover"
              onClick={
                () =>
                  onRecover?.(
                    advance
                  )
              }
              disabled={
                closed
              }
            >

              <FiRotateCcw />

              Recover

            </button>

          </footer>

        </aside>

      </div>,

      document.body

    );

};


const AmountBox = ({
  label,

  value,

  positive,

  warning,
}) => (

  <div
    className={[
      "employee-advance-amount-box",

      positive
        ?
        "positive"
        :
        "",

      warning
        ?
        "warning"
        :
        "",
    ]
      .filter(
        Boolean
      )
      .join(
        " "
      )}
  >

    <span>
      {label}
    </span>

    <strong>
      {
        formatEmployeeCurrency(
          value
        )
      }
    </strong>

  </div>

);


export default AdvanceDetailsDrawer;